import { ChartRangeOptions } from '@core/models/chart-range.model';
import moment from 'moment';
import { isoWeekOf } from '@core/helpers/display-time.helper';
import type { Moment } from 'moment';

export type TelemetryGranularity = 'quarter-hour' | 'hour' | 'week' | 'day' | 'month';

export interface TelemetryPeriod {
  granularity: TelemetryGranularity;
  period: string;
}

// The portal's range names the window on screen and the telemetry API's granularity names the
// slot, so the two are offset by one level: the Month button reads `day` slots and the Year
// button reads `month` slots. Renaming the enum to the API's words would make the buttons lie.
export const TELEMETRY_ROUTE: Record<
  ChartRangeOptions,
  { granularity: TelemetryGranularity; period: (date: Moment) => string }
> = {
  [ChartRangeOptions.DayPerQuarter]: {
    granularity: 'quarter-hour',
    period: date => date.format('YYYY-MM-DD'),
  },
  [ChartRangeOptions.Day]: { granularity: 'hour', period: date => date.format('YYYY-MM-DD') },
  // The API takes the ISO week-year, which differs from the calendar year in the week that
  // straddles New Year.
  [ChartRangeOptions.Week]: {
    granularity: 'week',
    period: date => {
      const { year, week } = isoWeekOf(date);
      return `${year}/${week}`;
    },
  },
  [ChartRangeOptions.Month]: {
    granularity: 'day',
    period: date => `${date.year()}/${date.month() + 1}`,
  },
  [ChartRangeOptions.Year]: { granularity: 'month', period: date => `${date.year()}` },
};

export function telemetryPeriod(range: ChartRangeOptions, date: Moment): TelemetryPeriod {
  const route = TELEMETRY_ROUTE[range];
  return { granularity: route.granularity, period: route.period(date) };
}

// The first day of a period, read back from the form the API takes.
const PERIOD_FORMAT: Record<TelemetryGranularity, string> = {
  'quarter-hour': 'YYYY-MM-DD',
  hour: 'YYYY-MM-DD',
  week: 'GGGG/W',
  day: 'YYYY/M',
  month: 'YYYY',
};

export function periodStart(period: TelemetryPeriod): Moment {
  return moment(period.period, PERIOD_FORMAT[period.granularity], true).startOf(
    period.granularity === 'week' ? 'isoWeek' : 'day',
  );
}

export function samePeriod(a: TelemetryPeriod | null, b: TelemetryPeriod | null): boolean {
  return !!a && !!b && a.granularity === b.granularity && a.period === b.period;
}
